import { forwardRef, memo } from 'react';
import type { InputHTMLAttributes } from 'react';
import { Checkbox as MuiCheckbox, type CheckboxProps as MuiCheckboxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import type { FormItemUiDefinition } from '../../../types/formItem.ts';

const StyledCheckbox = styled(MuiCheckbox, {
  shouldForwardProp: (prop) => prop !== 'formItemUiDefinition',
})<CheckboxProps>(({ theme, formItemUiDefinition }) => ({
  ...(formItemUiDefinition?.attributeColor && {
    backgroundColor: formItemUiDefinition.attributeColor,
  }),
  ...(formItemUiDefinition?.columnBorderColor && {
    border: `1px solid ${formItemUiDefinition.columnBorderColor}`,
  }),

  '&:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiSvgIcon-root': {
    position: 'relative',
    width: '20px',
    height: '20px',
    borderRadius: '6px',
    border: `2px solid ${theme.palette.secondary.main}`,
    backgroundColor: 'transparent',
    '& path': {
      display: 'none',
      transform: 'scale(0.7)',
      transformOrigin: 'center',
    },
  },
  '&.Mui-checked': {
    '& .MuiSvgIcon-root': {
      border: 'none',
      backgroundColor: theme.palette.primary.main,
      '& path': {
        display: 'block',
        color: 'white',
      },
    },
    '&:hover': {
      '& .MuiSvgIcon-root': {
        backgroundColor: alpha(theme.palette.primary.main, 0.8),
      },
    },
  },
  '&.Mui-disabled': {
    '& .MuiSvgIcon-root': {
      border: `2px solid ${theme.palette.action.disabled}`,
      opacity: 0.6,
    },
  },

  '&.Mui-checked.Mui-disabled': {
    '& .MuiSvgIcon-root': {
      border: 'none',
      backgroundColor: theme.palette.action.disabled,
      opacity: 0.6,
      '& path': {
        display: 'block',
        color: theme.palette.common.white,
      },
    },
  },
}));

type CheckboxProps = Omit<MuiCheckboxProps, 'icon' | 'checkedIcon'> & {
  formItemUiDefinition?: FormItemUiDefinition;
  dataTestId?: string;
};

type TestableInputProps = InputHTMLAttributes<HTMLInputElement> & {
  'data-testid'?: string;
};

const getTestableInputProps = (
  inputProps: InputHTMLAttributes<HTMLInputElement> | undefined,
  dataTestId: string | undefined
): TestableInputProps => ({
  ...inputProps,
  'data-testid': dataTestId,
});

export const Checkbox = memo(
  forwardRef<HTMLButtonElement, CheckboxProps>(({ dataTestId, inputProps, ...props }, ref) => {
    return (
      <StyledCheckbox ref={ref} {...props} inputProps={getTestableInputProps(inputProps, dataTestId)} icon={<Check />} checkedIcon={<Check />} />
    );
  })
);
